/*
 * Eufra — cart drawer, shipping country toggle, mobile menu and footer payments toggle.
 * The cart drawer is server-rendered by sections/eufra-cart-drawer.liquid; every cart change goes
 * through Shopify's AJAX Cart API and swaps in the freshly rendered section.
 */
(() => {
  const SECTION_ID = 'eufra-cart-drawer';
  const root = window.Shopify?.routes?.root || '/';

  class EufraCartDrawer extends HTMLElement {
    connectedCallback() {
      this.lastFocus = null;
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-eu-cart-open]');
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      });

      // Public API for Eufra sections: window.eufraCart.add([{ id, quantity }])
      window.eufraCart = {
        add: (items) => this.request('add', { items }).then((ok) => ok && this.open()),
      };

      // Horizon's product forms (product page, quick add) announce added lines with this event.
      document.addEventListener('shopify:cart:lines-update', () => {
        this.refresh().then(() => this.open());
      });

      // Restore the drawer after a back/forward navigation so it shows the current cart.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.refresh();
      });
    }

    get panel() {
      return this.querySelector('.eu-cart-panel');
    }

    open(opener) {
      this.lastFocus = opener || document.activeElement;
      this.classList.add('is-open');
      this.panel?.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      document.querySelectorAll('[data-eu-cart-open]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => this.querySelector('.eu-cart-close')?.focus());
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.panel?.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      document.querySelectorAll('[data-eu-cart-open]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
      if (this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
    }

    onClick(event) {
      const target = event.target.closest('button, [data-eu-cart-close]');
      if (!target || !this.contains(target)) return;

      if (target.matches('[data-eu-cart-close]')) {
        this.close();
        return;
      }

      const { dataset } = target;

      if (dataset.euLand) {
        setLand(dataset.euLand);
        return;
      }

      if (dataset.euLine) {
        const quantity = Number(dataset.euQuantity);
        this.request('change', { id: dataset.euLine, quantity });
        return;
      }

      if (dataset.euAdd) {
        this.request('add', { items: [{ id: Number(dataset.euAdd), quantity: 1 }] });
        return;
      }

      if (dataset.euUpgrade) {
        // { "<variant id>": quantity, … } — swaps the loose lash for its starter pack and
        // lowers loose Fixer/Cleanser lines that the pack now includes.
        this.request('update', { updates: JSON.parse(dataset.euUpgrade) });
      }
    }

    async request(action, body) {
      this.classList.add('is-busy');
      this.setError('');
      try {
        const response = await fetch(`${root}cart/${action}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: SECTION_ID, sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Cart error');
        }
        if (data.sections?.[SECTION_ID]) {
          this.render(data.sections[SECTION_ID]);
        } else {
          await this.refresh();
        }
        return true;
      } catch (error) {
        this.setError(error.message);
        this.open();
        return false;
      } finally {
        this.classList.remove('is-busy');
      }
    }

    async refresh() {
      const response = await fetch(`${window.location.pathname}?sections=${SECTION_ID}`);
      if (!response.ok) return;
      const data = await response.json();
      if (data[SECTION_ID]) this.render(data[SECTION_ID]);
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-eu-cart-content]');
      const current = this.querySelector('[data-eu-cart-content]');
      if (!next || !current) return;

      const bodyScroll = this.querySelector('.eu-cart-panel__body')?.scrollTop || 0;
      current.replaceWith(next);
      const body = this.querySelector('.eu-cart-panel__body');
      if (body) body.scrollTop = bodyScroll;

      const count = Number(next.dataset.euCount || 0);
      document.querySelectorAll('[data-eu-cart-count]').forEach((badge) => {
        badge.textContent = String(count);
        badge.hidden = count === 0;
      });

      if (this.classList.contains('is-open')) {
        this.panel?.setAttribute('aria-hidden', 'false');
      }
    }

    setError(message) {
      const error = this.querySelector('[data-eu-cart-error]');
      if (error) error.textContent = message;
    }
  }

  function setLand(land) {
    if (land !== 'NL' && land !== 'BE') return;
    document.documentElement.dataset.euLand = land;
    try {
      localStorage.setItem('eufra-land', land);
    } catch (e) {}
  }

  if (!customElements.get('eufra-cart-drawer')) {
    customElements.define('eufra-cart-drawer', EufraCartDrawer);
  }

  /*
   * Tabs: <div data-eu-tabs> with buttons [data-eu-tab="i"] (role="tab"), panels [data-eu-panel="i"]
   * (hidden when inactive) and cross-fading layers [data-eu-fade="i"] (class is-active). Videos inside
   * inactive layers are paused and marked data-lazy-hold so eufra-lazy-video.js leaves them alone.
   * Several tab groups can drive the same root (e.g. mobile + desktop copies).
   */
  function selectTab(root, index) {
    root.dataset.euActive = String(index);
    root.querySelectorAll('[data-eu-tab]').forEach((tab) => {
      const on = tab.dataset.euTab === String(index);
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
    });
    root.querySelectorAll('[data-eu-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.euPanel !== String(index);
    });
    root.querySelectorAll('[data-eu-fade]').forEach((layer) => {
      const on = layer.dataset.euFade === String(index);
      layer.classList.toggle('is-active', on);
      layer.querySelectorAll('video').forEach((video) => {
        if (on) {
          video.removeAttribute('data-lazy-hold');
          if (video.preload === 'none') video.preload = 'auto';
          try {
            video.currentTime = 0;
          } catch (e) {}
          if (!window.eufraVideoVisible || window.eufraVideoVisible(video)) video.play().catch(() => {});
        } else {
          video.setAttribute('data-lazy-hold', '');
          video.pause();
        }
      });
    });
    root.querySelectorAll('[data-eu-go]').forEach((go) => {
      go.classList.toggle('is-active', go.dataset.euGo === String(index));
    });
    // level bars (natuurlijk → glam): every bar up to the active one is filled
    root.querySelectorAll('[data-eu-bar]').forEach((bar) => {
      bar.classList.toggle('is-on', Number(bar.dataset.euBar) <= index);
    });
    root.dispatchEvent(new CustomEvent('eu:tab', { detail: { index }, bubbles: true }));
  }
  window.eufraSelectTab = selectTab;

  /*
   * Review slider (homepage "Een nieuwe lash routine"): auto-advances every 6s with a progress bar,
   * prev/next buttons, swipe, and one sound toggle per video — only the active slide can play sound.
   * Panels below the slider (product card, quote) are regular [data-eu-panel]s of the same tab root.
   */
  document.querySelectorAll('[data-eu-reviews]').forEach((root) => {
    const track = root.querySelector('[data-eu-rv-track]');
    const slides = [...root.querySelectorAll('[data-eu-rv-slide]')];
    const bars = [...root.querySelectorAll('[data-eu-rv-bar]')];
    const count = slides.length;
    const clones = [...root.querySelectorAll('[data-eu-rv-clone]')];
    // the slider always moves on its own, also when the OS asks for less motion (it is the main way to see the reviews)
    const reduce = false;
    let index = 0;
    let soundOn = null;
    let timer = null;

    const applySound = () => {
      slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        const button = slide.querySelector('[data-eu-rv-sound]');
        const on = soundOn === i && i === index;
        if (video) {
          video.muted = !on;
          video.volume = on ? 1 : 0;
        }
        button?.setAttribute('aria-pressed', String(on));
      });
    };

    const stepSize = () => slides[0].offsetWidth + parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 12);
    let snapTimer = null;

    const go = (i, auto) => {
      // going forward past the last slide slides on to the copy of the first one, then jumps back unnoticed
      const wrapForward = i === count && clones.length > 0;
      index = (i + count) % count;
      const step = stepSize();
      clearTimeout(snapTimer);
      track.style.transition = '';
      track.style.transform = `translateX(${-(wrapForward ? count : index) * step}px)`;
      slides.forEach((slide, n) => slide.classList.toggle('is-active', n === index && !wrapForward));
      clones.forEach((clone, n) => clone.classList.toggle('is-active', wrapForward && n === 0));
      if (wrapForward) {
        snapTimer = setTimeout(() => {
          track.style.transition = 'none';
          track.style.transform = 'translateX(0px)';
          clones[0].classList.remove('is-active');
          slides[0].classList.add('is-active');
          void track.offsetWidth;
          track.style.transition = '';
        }, 560);
      }
      bars.forEach((bar, n) => {
        bar.classList.remove('is-running');
        bar.style.width = n < index ? '100%' : '0%';
      });
      if (!reduce && bars[index]) {
        void bars[index].offsetWidth; // restart the fill animation
        bars[index].classList.add('is-running');
      } else if (bars[index]) {
        bars[index].style.width = '100%';
      }
      if (soundOn !== index) soundOn = null;
      applySound();
      selectTab(root, index);
      clearTimeout(timer);
      if (!reduce) timer = setTimeout(() => go(index + 1, true), 6100);
    };

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-eu-rv-prev]')) go(index - 1);
      if (event.target.closest('[data-eu-rv-next]')) go(index + 1);
      const sound = event.target.closest('[data-eu-rv-sound]');
      if (sound) {
        const n = slides.indexOf(sound.closest('[data-eu-rv-slide]'));
        if (n !== index) {
          soundOn = n;
          go(n);
        } else {
          soundOn = soundOn === n ? null : n;
          applySound();
        }
        const video = slides[n].querySelector('video');
        if (video?.paused) video.play().catch(() => {});
      }
      const slide = event.target.closest('[data-eu-rv-slide]');
      if (slide && !sound && !slide.hasAttribute('aria-hidden')) {
        const n = slides.indexOf(slide);
        if (n !== index) go(n);
      }
    });

    let start = null;
    const viewport = root.querySelector('[data-eu-rv-viewport]');
    viewport?.addEventListener('pointerdown', (event) => (start = { x: event.clientX, y: event.clientY }));
    viewport?.addEventListener('pointerup', (event) => {
      if (!start) return;
      const dx = event.clientX - start.x;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(event.clientY - start.y)) go(index + (dx < 0 ? 1 : -1));
      start = null;
    });

    // pause the auto-advance while the section is off screen
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) go(index);
        else clearTimeout(timer);
      });
    }).observe(root);
    window.addEventListener('resize', () => go(index));
  });

  // Links typed into theme settings (e.g. /pages/how-to-use) have no language prefix.
  // On /nl, /de or /fr, add it at click time so visitors stay in their language.
  const localeRoot = (window.eufraRoot || '/').replace(/\/?$/, '/');
  if (localeRoot !== '/') {
    const prefix = localeRoot.slice(0, -1);
    const skip = /^\/(cdn|checkout|checkouts|services|apps|account\/logout|localization|wpm)(\/|$)/;
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest('a[href^="/"]');
        if (!link || link.target === '_blank') return;
        const href = link.getAttribute('href');
        if (href.startsWith('//') || href === prefix || href.startsWith(localeRoot) || skip.test(href)) return;
        link.setAttribute('href', href === '/' ? localeRoot : prefix + href);
      },
      true
    );
  }

  // Accordions: [data-eu-accordion] with [data-eu-acc-btn] buttons; one item open at a time
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-eu-acc-btn]');
    if (!button) return;
    const root = button.closest('[data-eu-accordion]');
    const open = button.getAttribute('aria-expanded') !== 'true';
    if (root && !root.hasAttribute('data-eu-multi')) {
      root.querySelectorAll('[data-eu-acc-btn][aria-expanded="true"]').forEach((other) => {
        if (other !== button) other.setAttribute('aria-expanded', 'false');
      });
    }
    button.setAttribute('aria-expanded', String(open));
  });

  // Scroll carousels: [data-eu-carousel] > [data-eu-carousel-track] with prev/next buttons and mouse drag
  document.querySelectorAll('[data-eu-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-eu-carousel-track]');
    if (!track) return;
    const step = () => {
      const first = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 12;
      return first ? first.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    };
    carousel.addEventListener('click', (event) => {
      if (event.target.closest('[data-eu-carousel-prev]')) track.scrollBy({ left: -step(), behavior: 'smooth' });
      if (event.target.closest('[data-eu-carousel-next]')) track.scrollBy({ left: step(), behavior: 'smooth' });
    });
    let drag = null;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') return;
      drag = { x: event.clientX, left: track.scrollLeft };
      track.style.scrollSnapType = 'none';
      track.style.cursor = 'grabbing';
    });
    track.addEventListener('pointermove', (event) => {
      if (drag) track.scrollLeft = drag.left - (event.clientX - drag.x);
    });
    const end = () => {
      if (!drag) return;
      drag = null;
      track.style.cursor = '';
      const s = step();
      track.scrollTo({ left: Math.round(track.scrollLeft / s) * s, behavior: 'smooth' });
      setTimeout(() => (track.style.scrollSnapType = ''), 400);
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointerleave', end);
    track.addEventListener('dragstart', (event) => event.preventDefault());
  });

  // Before/after slider: drag anywhere on [data-eu-compare]; the handle is a keyboard slider too
  document.addEventListener('pointerdown', (event) => {
    const compare = event.target.closest('[data-eu-compare]');
    if (!compare) return;
    // inside a swipeable gallery only the handle drags, so the rest of the slide can still be swiped
    if (compare.dataset.euCompare === 'handle' && !event.target.closest('[role="slider"]')) return;
    const set = (e) => {
      const r = compare.getBoundingClientRect();
      const pct = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
      compare.style.setProperty('--seam', pct + '%');
      compare.querySelector('[role="slider"]')?.setAttribute('aria-valuenow', String(Math.round(pct)));
    };
    compare.setPointerCapture?.(event.pointerId);
    set(event);
    const move = (e) => set(e);
    const up = () => {
      compare.removeEventListener('pointermove', move);
      compare.removeEventListener('pointerup', up);
      compare.removeEventListener('pointercancel', up);
    };
    compare.addEventListener('pointermove', move);
    compare.addEventListener('pointerup', up);
    compare.addEventListener('pointercancel', up);
  });
  document.addEventListener('keydown', (event) => {
    const handle = event.target.closest?.('[data-eu-compare] [role="slider"]');
    if (!handle || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    const compare = handle.closest('[data-eu-compare]');
    const now = Number(handle.getAttribute('aria-valuenow') || 50);
    const pct = Math.max(0, Math.min(100, now + (event.key === 'ArrowRight' ? 5 : -5)));
    compare.style.setProperty('--seam', pct + '%');
    handle.setAttribute('aria-valuenow', String(pct));
  });

  // Swipe left/right on [data-eu-swipe] inside a tab root to go to the next/previous tab.
  // With data-eu-swipe-next / data-eu-swipe-prev (selectors inside the same section) it clicks those instead.
  // Touch events, because pointer events get cancelled as soon as the browser starts scrolling.
  let swipeStart = null;
  document.addEventListener(
    'touchstart',
    (event) => {
      const area = event.target.closest('[data-eu-swipe]');
      if (!area || event.touches.length !== 1) return (swipeStart = null);
      if (event.target.closest('[role="slider"], input, [data-eu-compare] .eu-pdp__handle')) return (swipeStart = null);
      const t = event.touches[0];
      swipeStart = { x: t.clientX, y: t.clientY, area };
    },
    { passive: true }
  );
  document.addEventListener(
    'touchend',
    (event) => {
      if (!swipeStart) return;
      const { x, y, area } = swipeStart;
      swipeStart = null;
      const t = event.changedTouches[0];
      const dx = t.clientX - x;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(t.clientY - y) * 1.2) return;
      const forward = dx < 0;
      const target = forward ? area.dataset.euSwipeNext : area.dataset.euSwipePrev;
      if (target) {
        const scope = area.closest('section, .shopify-section') || document;
        const button = scope.querySelector(target);
        if (button) button.click();
        return;
      }
      const root = area.closest('[data-eu-tabs]');
      if (!root) return;
      const count = new Set([...root.querySelectorAll('[data-eu-tab]')].map((el) => el.dataset.euTab)).size;
      if (!count) return;
      const current = Number(root.dataset.euActive || 0);
      selectTab(root, (current + (forward ? 1 : count - 1)) % count);
    },
    { passive: true }
  );
  document.addEventListener('touchcancel', () => (swipeStart = null), { passive: true });

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-eu-tab], [data-eu-go]');
    if (!tab) return;
    const root = tab.closest('[data-eu-tabs]');
    if (root) selectTab(root, Number(tab.dataset.euTab ?? tab.dataset.euGo));
  });

  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest?.('[role="tab"][data-eu-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    const list = [...tab.parentElement.querySelectorAll('[data-eu-tab]')];
    const next = list[(list.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : list.length - 1)) % list.length];
    next.focus();
    next.click();
  });

  // Add-to-cart buttons in Eufra sections: data-eu-add-items='[{"id":123,"quantity":1}]'
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-eu-add-items]');
    if (!button || !window.eufraCart) return;
    event.preventDefault();
    const items = JSON.parse(button.dataset.euAddItems);
    if (!items.length || !items.every((item) => item.id)) return;
    button.setAttribute('aria-busy', 'true');
    await window.eufraCart.add(items);
    button.removeAttribute('aria-busy');
  });

  // "Bestel binnen 2u 14m, morgen in huis" — counts down to the daily cut-off on weekdays
  const shipClocks = document.querySelectorAll('[data-eu-ship]');
  const tickShip = () => {
    const now = new Date();
    shipClocks.forEach((clock) => {
      const cutoff = new Date(now);
      cutoff.setHours(Number(clock.dataset.cutoff || 16), 0, 0, 0);
      const mins = Math.floor((cutoff - now) / 60000);
      const weekend = now.getDay() === 0 || now.getDay() === 6;
      const pre = clock.querySelector('[data-eu-ship-pre]');
      const time = clock.querySelector('[data-eu-ship-time]');
      const post = clock.querySelector('[data-eu-ship-post]');
      if (weekend || mins <= 0) {
        pre.textContent = clock.dataset.staticPre;
        time.textContent = clock.dataset.tomorrow;
        post.textContent = '';
      } else {
        const h = Math.floor(mins / 60);
        pre.textContent = clock.dataset.withinPre;
        time.textContent = (h ? h + clock.dataset.hours + ' ' : '') + (mins % 60) + clock.dataset.minutes;
        post.textContent = ', ' + clock.dataset.tomorrow;
      }
    });
  };
  if (shipClocks.length) {
    tickShip();
    setInterval(tickShip, 30000);
  }

  // Mobile menu
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-eu-menu-toggle]');
    if (!toggle) return;
    const nav = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!nav) return;
    const open = !nav.classList.contains('is-open');
    nav.classList.toggle('is-open', open);
    document.querySelectorAll(`[aria-controls="${nav.id}"]`).forEach((el) => el.setAttribute('aria-expanded', String(open)));
    document.documentElement.style.overflow = open ? 'hidden' : '';
    if (open) nav.querySelector('.eu-mobile-nav__close')?.focus();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const nav = document.querySelector('.eu-mobile-nav.is-open');
    if (nav) nav.querySelector('.eu-mobile-nav__close')?.click();
    document.querySelectorAll('.eu-lang[open]').forEach((details) => details.removeAttribute('open'));
  });

  // Links inside the mobile menu close it (anchors like #flexmag scroll on the same page)
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.eu-mobile-nav a, [data-eu-menu-close]');
    const nav = link && link.closest('.eu-mobile-nav');
    if (!nav || !nav.classList.contains('is-open')) return;
    nav.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    document.querySelectorAll(`[aria-controls="${nav.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
  });

  // Homepage on phones: scrolling down hides the bottom bar, scrolling up shows it and hides the header
  const bottomNav = document.querySelector('[data-eu-bottom-nav]');
  if (bottomNav) {
    const headerSection = document.querySelector('[data-eu-header]')?.closest('.shopify-section');
    const items = [...bottomNav.querySelectorAll('[data-eu-nav]')];
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - lastY;
      if (Math.abs(dy) > 6) {
        const nearEnd = y + window.innerHeight >= document.documentElement.scrollHeight - 160;
        bottomNav.classList.toggle('is-hidden', dy > 0 && y > 220 && !nearEnd);
        headerSection?.classList.toggle('is-hidden', y > 160 && dy < 0);
        lastY = y;
      }
      const probe = y + window.innerHeight * 0.45;
      const top = (id) => {
        const el = document.getElementById(id);
        return el ? el.getBoundingClientRect().top + y : Infinity;
      };
      let active = 'home';
      if (probe >= top('lash-selector')) active = 'lash-selector';
      if (probe >= top('reviews')) active = 'reviews';
      if (probe >= top('flexmag')) active = '';
      items.forEach((item) => item.setAttribute('aria-current', String(item.dataset.euNav === active)));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    bottomNav.querySelector('[data-eu-nav="home"]')?.addEventListener('click', (event) => {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // Language dropdown closes on outside click
  document.addEventListener('click', (event) => {
    document.querySelectorAll('.eu-lang[open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });

  const readJson = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key) || 'null');
    } catch (e) {
      return null;
    }
  };
  const writeJson = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {}
  };

  // Cookie banner — consent goes to Shopify's Customer Privacy API
  const cookies = document.querySelector('[data-eu-cookies]');
  if (cookies) {
    const KEY = 'eufra-cookies';
    const choice = { analytics: false, marketing: false };
    const prefsButton = cookies.querySelector('[data-eu-cookie-action="prefs"]');
    const prefsLabel = prefsButton.textContent;

    const open = () => {
      const saved = readJson(KEY);
      choice.analytics = !!saved?.a;
      choice.marketing = !!saved?.m;
      cookies.querySelectorAll('[data-eu-cookie-toggle]').forEach((toggle) => {
        toggle.setAttribute('aria-pressed', String(choice[toggle.dataset.euCookieToggle]));
      });
      cookies.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => cookies.classList.add('is-open')));
    };
    const close = () => {
      cookies.classList.remove('is-open', 'is-prefs');
      prefsButton.textContent = prefsLabel;
      setTimeout(() => (cookies.hidden = true), 450);
    };
    const save = (analytics, marketing) => {
      writeJson(KEY, { a: analytics, m: marketing, t: Date.now() });
      window.Shopify?.customerPrivacy?.setTrackingConsent(
        { analytics, marketing, preferences: true, sale_of_data: marketing },
        () => {}
      );
      close();
    };
    window.eufraOpenCookies = open;

    cookies.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-eu-cookie-toggle]');
      if (toggle) {
        const key = toggle.dataset.euCookieToggle;
        choice[key] = !choice[key];
        toggle.setAttribute('aria-pressed', String(choice[key]));
        return;
      }
      const action = event.target.closest('[data-eu-cookie-action]')?.dataset.euCookieAction;
      if (action === 'all') save(true, true);
      if (action === 'necessary') save(false, false);
      if (action === 'prefs') {
        if (cookies.classList.contains('is-prefs')) {
          save(choice.analytics, choice.marketing);
        } else {
          cookies.classList.add('is-prefs');
          prefsButton.textContent = prefsButton.dataset.saveLabel;
        }
      }
    });
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-eu-open-cookies], a[href$="#cookie-instellingen"]')) {
        event.preventDefault();
        open();
      }
    });

    const decide = () => {
      const privacy = window.Shopify?.customerPrivacy;
      if (!privacy) {
        if (!readJson(KEY)) open();
        return;
      }
      const undecided = privacy.getTrackingConsent?.() === 'no_interaction';
      if (privacy.shouldShowBanner?.() || (undecided && !readJson(KEY))) open();
    };
    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error) window.Shopify.customerPrivacy = undefined;
        decide();
      });
    } else {
      decide();
    }
  }

  // Header panels ("Magnetische Wimpers", "Starterpacks"): hover opens them in CSS; click, keyboard
  // and touch use is-open
  document.querySelectorAll('[data-eu-drop]').forEach((drop) => {
    const toggle = drop.querySelector('.eu-drop__toggle');
    const setOpen = (open) => {
      drop.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
    };
    toggle.addEventListener('click', () => setOpen(!drop.classList.contains('is-open')));
    drop.addEventListener('focusin', (event) => {
      if (event.target !== toggle) setOpen(true);
    });
    drop.addEventListener('focusout', (event) => {
      if (!drop.contains(event.relatedTarget)) setOpen(false);
    });
    drop.addEventListener('mouseleave', () => {
      if (!drop.contains(document.activeElement)) setOpen(false);
    });
    document.addEventListener('click', (event) => {
      if (!drop.contains(event.target)) setOpen(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !drop.classList.contains('is-open')) return;
      setOpen(false);
      toggle.focus();
    });
  });

  // Footer payment methods: "+ meer" / "− minder"
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-eu-pay-toggle]');
    if (!toggle) return;
    const wrap = toggle.closest('[data-eu-pay-wrap]') || toggle.closest('.eu-footer__payments');
    const open = !wrap.classList.contains('is-open');
    wrap.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.textContent = open ? toggle.dataset.lessLabel : toggle.dataset.moreLabel;
  });
})();
